import { useSelector } from "react-redux";
import {
  getConsignmentById,
  getCurrentSaleType,
  getCurrentSpeciesId,
  getHasWriteAccessInCurrentSale,
  getSaleLotById,
} from "selectors";
import { getSaleLotStatus } from "lib/saleLot";
import { hasPermission } from "lib/permissions";
import {
  ConsignmentPermissions,
  SaleLotPermissions,
} from "constants/permissions";
import { useHasManualChargesPermission } from "hooks";
import { Species } from "constants/species";
import { LivestockSaleTypes, saleLotStatuses, SaleType } from "constants/sale";
import { ExportSite } from "constants/exportSites";

// A canonical list of all possible buttons that can appear in a Lot Card's action menu
export enum LotButtonOption {
  VIEW,
  EDIT,
  ADD_BUYER_SUNDRY,
  ADD_INVOICE_TO_MANUAL_CHARGE,
  ADD_THIRD_PARTY_SUNDRY,
  SCAN,
  COPY_DRAFT,
  ADD_MEDIA,
  SPLIT_LOT,
  PEN,
  REPEN,
  UNPEN,
  SELL,
  VIEW_SALE,
  UNSELL,
  DELIVER,
  UNDELIVER,
  PRINT_WEIGHT_SHEET,
  PRINT_PLACARD,
}

// The default order in which buttons should appear in the action menu
export const LotButtonDefaultOrder: LotButtonOption[] = [
  LotButtonOption.VIEW,
  LotButtonOption.EDIT,
  LotButtonOption.ADD_BUYER_SUNDRY,
  LotButtonOption.ADD_INVOICE_TO_MANUAL_CHARGE,
  LotButtonOption.ADD_THIRD_PARTY_SUNDRY,
  LotButtonOption.SCAN,
  LotButtonOption.COPY_DRAFT,
  LotButtonOption.ADD_MEDIA,
  LotButtonOption.SPLIT_LOT,
  LotButtonOption.PEN,
  LotButtonOption.REPEN,
  LotButtonOption.UNPEN,
  LotButtonOption.SELL,
  LotButtonOption.VIEW_SALE,
  LotButtonOption.UNSELL,
  LotButtonOption.DELIVER,
  LotButtonOption.UNDELIVER,
  LotButtonOption.PRINT_WEIGHT_SHEET,
  LotButtonOption.PRINT_PLACARD,
];

export function useGetLotButtonOptions(saleLotId: string): LotButtonOption[] {
  const options: LotButtonOption[] = [];

  const hasWriteAccessInCurrentSale = useSelector(
    getHasWriteAccessInCurrentSale,
  );

  const saleLot: SaleLot = useSelector(getSaleLotById(saleLotId));
  const consignment: Consignment = useSelector(
    getConsignmentById(saleLot.consignment_id),
  );
  const saleType = useSelector<State, SaleType>(getCurrentSaleType);
  const status = getSaleLotStatus(saleLot, consignment, saleType);

  const { total_mass_grams: totalMassGrams } = saleLot;

  const hasEditSaleLotPermission = hasPermission(
    saleLot,
    SaleLotPermissions.update,
  );

  const hasAddSaleLotPermission = hasPermission(
    consignment,
    ConsignmentPermissions.canAddSaleLot,
  );

  const hasManualChargePermission = useHasManualChargesPermission();

  const isCattleSale =
    useSelector<State, number>(getCurrentSpeciesId) === Species.CATTLE;
  const isLivestockSale = LivestockSaleTypes.includes(saleType);

  const isListedWithAuctionsPlus =
    saleLot.exportSites &&
    saleLot.exportSites.includes(ExportSite.AUCTIONS_PLUS);

  if (hasEditSaleLotPermission) {
    options.push(LotButtonOption.EDIT);

    if (isListedWithAuctionsPlus) {
      options.push(LotButtonOption.ADD_MEDIA);
    }
  } else {
    options.push(LotButtonOption.VIEW);
  }

  if (hasManualChargePermission && saleLot.buyer_id) {
    options.push(LotButtonOption.ADD_BUYER_SUNDRY);
  }
  if (hasManualChargePermission && saleLot.invoiceToBusinessId) {
    options.push(LotButtonOption.ADD_INVOICE_TO_MANUAL_CHARGE);
  }
  if (hasManualChargePermission && saleLot.thirdPartyId) {
    options.push(LotButtonOption.ADD_THIRD_PARTY_SUNDRY);
  }
  if (hasWriteAccessInCurrentSale) {
    if (isLivestockSale) {
      options.push(LotButtonOption.SCAN);
    }

    if (hasAddSaleLotPermission) {
      options.push(LotButtonOption.COPY_DRAFT);
      options.push(LotButtonOption.SPLIT_LOT);
    }

    if (status === saleLotStatuses.PENNED) {
      options.push(LotButtonOption.REPEN);
      options.push(LotButtonOption.UNPEN);
      options.push(LotButtonOption.SELL);
    } else if (status === saleLotStatuses.NOT_PENNED) {
      options.push(LotButtonOption.PEN);
    } else if (
      status === saleLotStatuses.SOLD ||
      status === saleLotStatuses.NO_SALE
    ) {
      options.push(LotButtonOption.VIEW_SALE);
      options.push(LotButtonOption.UNSELL);
      options.push(LotButtonOption.DELIVER);
    } else if (status === saleLotStatuses.DELIVERED) {
      options.push(LotButtonOption.UNDELIVER);
    }
  }

  if (totalMassGrams > 0) {
    options.push(LotButtonOption.PRINT_WEIGHT_SHEET);
  }

  if (isCattleSale) {
    options.push(LotButtonOption.PRINT_PLACARD);
  }

  return options;
}
